import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { MasterResidentialRoom, OrgResidentialRoom, RoomProfile } from '../types/admin';

// ── Master Rooms ─────────────────────────────────────────────────────────────

export interface RoomInput {
  name: string;
  profiles: RoomProfile[];
}

export function useMasterResidentialRooms() {
  const [rooms, setRooms] = useState<MasterResidentialRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRooms = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ success: boolean; data: MasterResidentialRoom[] }>(
        '/admin/master-residential-rooms',
      );
      setRooms(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load rooms');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  const createRoom = async (input: RoomInput): Promise<MasterResidentialRoom> => {
    const res = await api.post<{ success: boolean; data: MasterResidentialRoom }>(
      '/admin/master-residential-rooms',
      input,
    );
    setRooms(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
    return res.data;
  };

  const updateRoom = async (id: string, input: Partial<RoomInput>): Promise<MasterResidentialRoom> => {
    const res = await api.put<{ success: boolean; data: MasterResidentialRoom }>(
      `/admin/master-residential-rooms/${id}`,
      input,
    );
    setRooms(prev => prev.map(r => (r.id === id ? res.data : r)));
    return res.data;
  };

  const deleteRoom = async (id: string): Promise<void> => {
    await api.del(`/admin/master-residential-rooms/${id}`);
    setRooms(prev => prev.filter(r => r.id !== id));
  };

  const reorderRooms = async (order: { id: string; sortOrder: number }[]): Promise<void> => {
    await api.put('/admin/master-residential-rooms/reorder', { order });
    // Update local state optimistically
    setRooms(prev => {
      const orderMap = new Map(order.map(o => [o.id, o.sortOrder]));
      const updated = prev.map(r => {
        const so = orderMap.get(r.id);
        return so != null ? { ...r, sortOrder: so } : r;
      });
      return updated.sort((a, b) => {
        const oa = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
        const ob = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
        if (oa !== ob) return oa - ob;
        return a.name.localeCompare(b.name);
      });
    });
  };

  const bulkUpload = async (rooms: unknown[]): Promise<{ created: number; updated: number }> => {
    const res = await api.post<{ success: boolean; data: { created: number; updated: number } }>(
      '/admin/master-residential-rooms/bulk-upload',
      { rooms },
    );
    await fetchRooms();
    return res.data;
  };

  return { rooms, loading, error, createRoom, updateRoom, deleteRoom, reorderRooms, bulkUpload, refetch: fetchRooms };
}

// ── Org Rooms ────────────────────────────────────────────────────────────────

export function useOrgResidentialRooms(orgId: string) {
  const [rooms, setRooms] = useState<OrgResidentialRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRooms = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ success: boolean; data: OrgResidentialRoom[] }>(
        `/org-admin/${orgId}/residential-rooms`,
      );
      setRooms(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load rooms');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  const createRoom = async (input: RoomInput): Promise<OrgResidentialRoom> => {
    const res = await api.post<{ success: boolean; data: OrgResidentialRoom }>(
      `/org-admin/${orgId}/residential-rooms`,
      input,
    );
    setRooms(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
    return res.data;
  };

  const updateRoom = async (id: string, input: Partial<RoomInput>): Promise<OrgResidentialRoom> => {
    const res = await api.put<{ success: boolean; data: OrgResidentialRoom }>(
      `/org-admin/${orgId}/residential-rooms/${id}`,
      input,
    );
    setRooms(prev => prev.map(r => (r.id === id ? res.data : r)));
    return res.data;
  };

  const deleteRoom = async (id: string): Promise<void> => {
    await api.del(`/org-admin/${orgId}/residential-rooms/${id}`);
    setRooms(prev => prev.filter(r => r.id !== id));
  };

  const syncFromMaster = async (): Promise<{ added: number; updated: number }> => {
    const res = await api.post<{ success: boolean; data: { added: number; updated: number } }>(
      `/org-admin/${orgId}/residential-rooms/sync-from-master`,
    );
    await fetchRooms();
    return res.data;
  };

  const reorderRooms = async (order: { id: string; sortOrder: number }[]): Promise<void> => {
    await api.put(`/org-admin/${orgId}/residential-rooms/reorder`, { order });
    setRooms(prev => {
      const orderMap = new Map(order.map(o => [o.id, o.sortOrder]));
      const updated = prev.map(r => {
        const so = orderMap.get(r.id);
        return so != null ? { ...r, sortOrder: so } : r;
      });
      return updated.sort((a, b) => {
        const oa = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
        const ob = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
        if (oa !== ob) return oa - ob;
        return a.name.localeCompare(b.name);
      });
    });
  };

  return { rooms, loading, error, createRoom, updateRoom, deleteRoom, syncFromMaster, reorderRooms, refetch: fetchRooms };
}
